import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { LayoutDashboard, Users, Send, History, MessageSquare, School, UserCog, FileText, Inbox, BarChart3 } from "lucide-react";

import DashboardLayout from "@/components/DashboardLayout";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Schools from "@/pages/admin/Schools";
import Counselors from "@/pages/admin/Counselors";
import AdminStudents from "@/pages/admin/AdminStudents";
import Trials from "@/pages/admin/Trials";
import CounselorDashboard from "@/pages/counselor/CounselorDashboard";
import StudentsPage from "@/pages/counselor/StudentsPage";
import SendMessage from "@/pages/counselor/SendMessage";
import Templates from "@/pages/counselor/Templates";
import Reports from "@/pages/counselor/Reports";
import Logs from "@/pages/counselor/Logs";
import WhatsAppPage from "@/pages/counselor/WhatsAppPage";

const adminNav = [
  { key: "dashboard", to: "/admin", end: true, label: "الرئيسية", icon: LayoutDashboard },
  { key: "schools", to: "/admin/schools", label: "المدارس", icon: School },
  { key: "counselors", to: "/admin/counselors", label: "المرشدون", icon: UserCog },
  { key: "students", to: "/admin/students", label: "بيانات الطلاب", icon: Users },
  { key: "trials", to: "/admin/trials", label: "طلبات التجربة", icon: Inbox },
];

const counselorNav = [
  { key: "dashboard", to: "/app", end: true, label: "الرئيسية", icon: LayoutDashboard },
  { key: "students", to: "/app/students", label: "الطلاب", icon: Users },
  { key: "send", to: "/app/send", label: "إرسال رسالة", icon: Send },
  { key: "templates", to: "/app/templates", label: "القوالب", icon: FileText },
  { key: "logs", to: "/app/logs", label: "سجل الرسائل", icon: History },
  { key: "reports", to: "/app/reports", label: "التقارير", icon: BarChart3 },
  { key: "whatsapp", to: "/app/whatsapp", label: "ربط واتساب", icon: MessageSquare },
];

function Protected({ role, children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role)
    return <Navigate to={user.role === "SUPER_ADMIN" ? "/admin" : "/app"} replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "SUPER_ADMIN" ? "/admin" : "/app"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />

            <Route
              element={
                <Protected role="SUPER_ADMIN">
                  <DashboardLayout items={adminNav} title="لوحة التحكم" />
                </Protected>
              }
            >
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/schools" element={<Schools />} />
              <Route path="/admin/counselors" element={<Counselors />} />
              <Route path="/admin/students" element={<AdminStudents />} />
              <Route path="/admin/trials" element={<Trials />} />
            </Route>

            <Route
              element={
                <Protected role="COUNSELOR">
                  <DashboardLayout items={counselorNav} title="لوحة المرشد" />
                </Protected>
              }
            >
              <Route path="/app" element={<CounselorDashboard />} />
              <Route path="/app/students" element={<StudentsPage />} />
              <Route path="/app/send" element={<SendMessage />} />
              <Route path="/app/templates" element={<Templates />} />
              <Route path="/app/logs" element={<Logs />} />
              <Route path="/app/reports" element={<Reports />} />
              <Route path="/app/whatsapp" element={<WhatsAppPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
